import { Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { CartOfferLine, selectCurrency, selectOffers } from '~shared/modules/shopping-cart-store';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { DESKTOP_CRITEO_ID, MOBILE_CRITEO_ID } from '~app/modules/checkout/constants';
import { CheckoutCommonUtil } from '~app/modules/checkout/utils/checkout-common-util';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { select, Store } from '@ngrx/store';
import { State } from '~app/modules/checkout/store/countries';
import { ServerLoggerService } from '~shared/modules/server-logger/services/server-logger.service';
import { MEDIA_ALISES } from '~shared/constants/media.constant';
import { MediaObserver } from '@angular/flex-layout';
import { environment } from '~env/environment';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_CART_CRITEO_ON_THANK_YOU_PAGE_V2 } from '~app/modules/checkout/constants/checkout-feature-flags';
import { CookieService } from '~shared/services/cookie.service';
import { CriteoItems, CriteoTransactionParams } from '~app/modules/checkout/models/criteo.model';

@Injectable({
  providedIn: 'root',
})
export class CriteoService {
  constructor(
    private handler: HttpBackend,
    private store$: Store<State>,
    private accountInfoService: AccountInfoService,
    private serverLoggerService: ServerLoggerService,
    private mediaObserver: MediaObserver,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService,
    private cookieService: CookieService
  ) {
    this.httpClient = new HttpClient(handler);
  }
  private httpClient: HttpClient;
  private baseURL = environment.criteoBaseUrl();
  private eventType = 'trackTransaction';
  private currency$ = this.store$.pipe(select(selectCurrency));
  private offerLines$ = this.store$.pipe(select(selectOffers));
  private isMobile = this.mediaObserver.isActive(MEDIA_ALISES.mobile);

  private getQuantitiesArray(offerLines: CartOfferLine[]) {
    return offerLines.map((offerLine) => offerLine.quantity.toString());
  }
  private getPriceGrossArray(offerLines: CartOfferLine[]) {
    return offerLines.map((offerLine) => (+offerLine.price.offer + +offerLine.price.offerTax).toFixed(2).toString());
  }

  private getArrayOfItemIdAndSellerId(offerLines: CartOfferLine[]) {
    return offerLines.map((offerLine) => ({ itemId: offerLine.item.productId, sellerId: offerLine.seller.id }));
  }
  private arrayToStringWithPipe(array: string[]): string {
    return array.join('|');
  }

  private itemsToMappedString(items: CriteoItems[]) {
    const mappedItems = items.map((item) => `${item.itemId}_${item.sellerId}`);
    return this.arrayToStringWithPipe(mappedItems);
  }

  private setTransactionData(transactionData: CriteoTransactionParams) {
    CheckoutCommonUtil.saveTmpCriteoTransactionDataOnSessionStorage(transactionData);
  }

  private createCriteoUrl(transactionData: CriteoTransactionParams) {
    return `${this.baseURL}?criteo-partner-id=${transactionData.partnerId}
    &retailer-visitor-id=${transactionData.retailerVisitorId}
    &customer-id=${transactionData.customerId}
    &event-type=${this.eventType}
    &page-id=${transactionData.pageId}
    &item=${this.itemsToMappedString(transactionData.items)}
    &price=${this.arrayToStringWithPipe(transactionData.prices)}
    &quantity=${this.arrayToStringWithPipe(transactionData.quantities)}
    &transaction-id=${transactionData.transactionId}
    &currency=${transactionData.currency ?? 'EUR'}
    `
      .split(/\s+/)
      .join('');
  }

  initialize() {
    combineLatest([
      this.offerLines$,
      this.currency$,
      this.accountInfoService.getAccountInfo(),
      this.activeFeaturesConfigCatService.getFeatureValue(FF_CART_CRITEO_ON_THANK_YOU_PAGE_V2),
    ])
      .pipe(take(1))
      .subscribe(([offerLines, currency, accountInfo, criteoOptions]) => {
        const { partnerId } = JSON.parse(criteoOptions);
        const retailerVisitorId = this.cookieService.getCriteoRetailerId();
        if (partnerId && retailerVisitorId) {
          this.setTransactionData({
            retailerVisitorId: this.cookieService.getCriteoRetailerId(),
            partnerId,
            customerId: accountInfo?.hashedPersonCdmId,
            pageId: this.isMobile ? MOBILE_CRITEO_ID : DESKTOP_CRITEO_ID,
            items: this.getArrayOfItemIdAndSellerId(offerLines),
            prices: this.getPriceGrossArray(offerLines),
            quantities: this.getQuantitiesArray(offerLines),
            transactionId: CheckoutCommonUtil.getTmpOrderNumberFromLocalStorage(),
            currency,
          });
        }
      });
  }

  sendTrackTransactionEvent(): void {
    const transactionData = CheckoutCommonUtil.getTmpCriteoTransactionDataOnSessionStorage();

    if (transactionData?.partnerId && transactionData?.retailerVisitorId) {
      const url = this.createCriteoUrl(transactionData);

      this.httpClient.get(url).subscribe({
        next: () => {
          CheckoutCommonUtil.clearTmpCriteoTransactionDataOnSessionStorage();
        },
        error: (error) => {
          CheckoutCommonUtil.clearTmpCriteoTransactionDataOnSessionStorage();
          this.serverLoggerService.error('Error sending Criteo track transaction event:', error);
        },
      });
    }
  }
}
